import React from "react";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EastIcon from "@mui/icons-material/East";
import SouthIcon from "@mui/icons-material/South";
import { Box, IconButton, Stack, Typography } from "@mui/material";

import { connectionSectionDeleteButtonSx } from "../shared/connectionSectionActionStyles";

interface MappingRowShellProps {
    source: React.ReactNode;
    options: React.ReactNode;
    destination: React.ReactNode;
    onDelete?: () => void;
    canDelete?: boolean;
    sourceCaption?: string;
    destinationCaption?: string;
}

const captionSx = {
    display: { xs: "block", md: "none" },
    fontSize: "11px",
    fontWeight: 600,
    color: "#6B6B6B",
    mb: 0.75,
    fontFamily: '"Nunito", sans-serif',
    textAlign: "center",
} as const;

const columnSx = {
    flex: 1,
    minWidth: 0,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    "& > *": {
        width: "100%",
        maxWidth: "100%",
    },
} as const;

const MappingRowShell: React.FC<MappingRowShellProps> = ({
    source,
    options,
    destination,
    onDelete,
    canDelete = false,
    sourceCaption = "Source Field",
    destinationCaption = "Destination Field",
}) => {
    return (
        <Box
            sx={{
                px: { xs: 1.5, md: 2 },
                py: { xs: 1.5, md: 1.75 },
                borderRadius: "10px",
                backgroundColor: "#fff",
                border: "1px solid rgba(0, 0, 0, 0.05)",
                transition:
                    "background-color 0.15s ease, border-color 0.15s ease",
                "&:hover": {
                    backgroundColor: "#fafafa",
                    borderColor: "#e5e5ea",
                },
            }}
        >
            <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "stretch", md: "center" }}
                gap={{ xs: 1.5, md: 2 }}
            >
                <Box sx={columnSx}>
                    <Typography sx={captionSx}>{sourceCaption}</Typography>
                    {source}
                </Box>

                <Stack
                    direction={{ xs: "column", md: "row" }}
                    alignItems="center"
                    justifyContent="center"
                    gap={{ xs: 1, md: 1.5 }}
                    sx={{
                        flexShrink: 0,
                        alignSelf: "center",
                        width: { xs: "100%", md: "auto" },
                        minWidth: { md: 120 },
                    }}
                >
                    <Box
                        sx={{
                            display: { xs: "none", md: "flex" },
                            color: "#c1c1c1",
                            alignItems: "center",
                            justifyContent: "center",
                            flex: 1,
                            minWidth: 24,
                        }}
                        aria-hidden
                    >
                        <EastIcon fontSize="small" />
                    </Box>
                    <Box
                        sx={{
                            display: { xs: "flex", md: "none" },
                            color: "#c1c1c1",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                        }}
                        aria-hidden
                    >
                        <SouthIcon fontSize="small" />
                    </Box>
                    <Box
                        sx={{
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: { xs: "100%", md: "auto" },
                            flexShrink: 0,
                        }}
                    >
                        {options}
                    </Box>
                    <Box
                        sx={{
                            display: { xs: "none", md: "flex" },
                            color: "#c1c1c1",
                            alignItems: "center",
                            justifyContent: "center",
                            flex: 1,
                            minWidth: 24,
                        }}
                        aria-hidden
                    >
                        <EastIcon fontSize="small" />
                    </Box>
                </Stack>

                <Box sx={columnSx}>
                    <Typography sx={captionSx}>{destinationCaption}</Typography>
                    {destination}
                </Box>

                {canDelete && onDelete ? (
                    <IconButton
                        size="small"
                        onClick={onDelete}
                        aria-label="Remove mapping"
                        sx={{
                            ...connectionSectionDeleteButtonSx,
                            alignSelf: { xs: "flex-end", md: "center" },
                        }}
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                ) : null}
            </Stack>
        </Box>
    );
};

export default MappingRowShell;
